import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import BackgroundEffects from '../../../components/layout/BackgroundEffects/BackgroundEffects'
import Header from '../../../components/layout/Header/Header'
import Card from '../../../components/common/Card/Card'
import Button from '../../../components/common/Button/Button'
import { getCurrentUser, logout, getUserData } from '../../../services/authService'
import {
  getAllAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement
} from '../../../services/announcementsService'
import AnnouncementCard from '../components/AnnouncementCard'
import AnnouncementModal from '../components/AnnouncementModal'

const Announcements = () => {
  const navigate = useNavigate()

  // Auth & User State
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  // Announcements State
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Load user data and announcements
  useEffect(() => {
    loadUserAndAnnouncements()
  }, [])

  const loadUserAndAnnouncements = async () => {
    try {
      setLoading(true)
      setError('')

      const currentUser = getCurrentUser()
      if (!currentUser) {
        navigate('/login')
        return
      }

      setUser(currentUser)

      // Get user data from Firestore
      const userDataResult = await getUserData(currentUser.uid)

      if (!userDataResult.success) {
        setError('Failed to load user data')
        setLoading(false)
        return
      }

      setUserData(userDataResult.data)

      // Check if user is admin
      const adminStatus =
        userDataResult.data.role === 'super_admin' ||
        userDataResult.data.role === 'admin'
      setIsAdmin(adminStatus)

      // Load announcements
      const result = await getAllAnnouncements()
      if (result.success) {
        setAnnouncements(result.data)
      } else {
        setError(result.error || 'Failed to load announcements')
      }
    } catch (error) {
      console.error('Load error:', error)
      setError('An error occurred while loading data')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    const result = await logout()
    if (result.success) {
      navigate('/login')
    }
  }

  const handleBack = () => {
    navigate('/dashboard')
  }

  const handleOpenModal = () => {
    setEditingAnnouncement(null)
    setIsModalOpen(true)
  }

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingAnnouncement(null)
  }

  const handleSubmitAnnouncement = async (formData) => {
    setIsSubmitting(true)
    setError('')
    setSuccessMessage('')

    try {
      if (editingAnnouncement) {
        // Update existing announcement
        const result = await updateAnnouncement(editingAnnouncement.id, formData)

        if (result.success) {
          // Update in local state
          setAnnouncements(prev =>
            prev.map(ann =>
              ann.id === editingAnnouncement.id
                ? { ...ann, ...formData }
                : ann
            )
          )
          setSuccessMessage('Announcement updated successfully!')
          handleCloseModal()
        } else {
          setError(result.error || 'Failed to update announcement')
        }
      } else {
        // Create new announcement
        const announcementData = {
          ...formData,
          postedBy: user.uid,
          postedByName: userData.fullName || user.email
        }

        const result = await createAnnouncement(announcementData)

        if (result.success) {
          // Add to local state at the beginning
          setAnnouncements(prev => [result.data, ...prev])
          setSuccessMessage('Announcement posted successfully!')
          handleCloseModal()
        } else {
          setError(result.error || 'Failed to create announcement')
        }
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error) {
      console.error('Submit error:', error)
      setError('An error occurred while saving the announcement')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteAnnouncement = async (id) => {
    try {
      const result = await deleteAnnouncement(id)

      if (result.success) {
        // Remove from local state
        setAnnouncements(prev => prev.filter(ann => ann.id !== id))
        setSuccessMessage('Announcement deleted successfully!')
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to delete announcement')
        setTimeout(() => setError(''), 5000)
      }
    } catch (error) {
      console.error('Delete error:', error)
      setError('An error occurred while deleting the announcement')
      setTimeout(() => setError(''), 5000)
    }
  }

  // Loading State
  if (loading) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Announcements"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-spinner fa-spin"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-primary)',
              marginBottom: '1rem'
            }}
          ></i>
          <p style={{ color: 'var(--psyber-text-secondary)' }}>Loading announcements...</p>
        </Card>
      </div>
    )
  }

  // Error State (Critical Error)
  if (error && announcements.length === 0 && !successMessage) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Announcements"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-exclamation-triangle"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-error)',
              marginBottom: '1rem'
            }}
          ></i>
          <h3 style={{ marginBottom: '1rem', color: 'var(--psyber-error)' }}>Error</h3>
          <p style={{ color: 'var(--psyber-text-secondary)', marginBottom: '2rem' }}>{error}</p>
          <Button variant="primary" icon="fas fa-redo" onClick={loadUserAndAnnouncements}>
            Retry
          </Button>
        </Card>
      </div>
    )
  }

  // Empty State
  const EmptyState = () => (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <i
        className="fas fa-inbox"
        style={{
          fontSize: '4rem',
          color: 'var(--psyber-text-muted)',
          marginBottom: '1.5rem',
          display: 'block'
        }}
      ></i>
      <h3 style={{ marginBottom: '0.5rem', color: 'var(--psyber-text)' }}>
        No Announcements Yet
      </h3>
      <p style={{ color: 'var(--psyber-text-secondary)' }}>
        {isAdmin
          ? 'Click "Post Announcement" to create your first announcement'
          : 'Check back later for company announcements and updates'}
      </p>
    </div>
  )

  return (
    <div className="psyber-container psyber-portal-container">
      <BackgroundEffects />

      <Header
        user={userData || user}
        onLogout={handleLogout}
        showBackButton={true}
        onBack={handleBack}
        title="Announcements"
      />

      {/* Success Message */}
      {successMessage && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-success-bg)',
            border: '2px solid var(--psyber-success)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-check-circle" style={{ color: 'var(--psyber-success)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-success)', fontWeight: 600 }}>{successMessage}</p>
          </div>
        </Card>
      )}

      {/* Error Message */}
      {error && announcements.length > 0 && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-error-bg)',
            border: '2px solid var(--psyber-error)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-exclamation-circle" style={{ color: 'var(--psyber-error)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</p>
          </div>
        </Card>
      )}

      {/* Main Content Card */}
      <Card>
        {/* Section Header */}
        <div
          style={{
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '2px solid var(--psyber-card-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem'
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--psyber-font-size-2xl)',
                color: 'var(--psyber-text)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}
            >
              <i className="fas fa-bullhorn"></i>
              Company Announcements
            </h2>
            <p
              style={{
                margin: '0.5rem 0 0 0',
                fontSize: 'var(--psyber-font-size-sm)',
                color: 'var(--psyber-text-secondary)'
              }}
            >
              {announcements.length === 0
                ? 'No announcements to display'
                : `${announcements.length} announcement${announcements.length !== 1 ? 's' : ''} available`}
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Button variant="ghost" icon="fas fa-sync" onClick={loadUserAndAnnouncements}>
              Refresh
            </Button>
            {isAdmin && (
              <Button
                variant="primary"
                icon="fas fa-plus"
                onClick={handleOpenModal}
              >
                Post Announcement
              </Button>
            )}
          </div>
        </div>

        {/* Announcements Grid or Empty State */}
        {announcements.length === 0 ? (
          <EmptyState />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))',
              gap: '1.5rem'
            }}
          >
            {announcements.map((announcement) => (
              <AnnouncementCard
                key={announcement.id}
                announcement={announcement}
                isAdmin={isAdmin}
                onEdit={handleEditAnnouncement}
                onDelete={handleDeleteAnnouncement}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Help Section for Non-Admins */}
      {!isAdmin && announcements.length > 0 && (
        <Card style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <i className="fas fa-info-circle"></i> About Announcements
          </h3>
          <div
            style={{
              fontSize: 'var(--psyber-font-size-sm)',
              color: 'var(--psyber-text-secondary)',
              lineHeight: 1.6
            }}
          >
            <p style={{ marginBottom: '0.75rem' }}>
              <strong>Categories:</strong> Announcements are organized by category (General, HR, IT, Urgent) to help you find relevant information.
            </p>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong>Urgent Announcements:</strong> Highlighted with a red border and require immediate attention.
            </p>
            <p style={{ margin: 0 }}>
              <strong>Stay Updated:</strong> Check this page regularly for important company news and updates.
            </p>
          </div>
        </Card>
      )}

      {/* Announcement Modal */}
      <AnnouncementModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitAnnouncement}
        announcement={editingAnnouncement}
        isLoading={isSubmitting}
      />
    </div>
  )
}

export default Announcements
